import { Link } from 'react-router-dom';
import { NudosHoverText } from '../../../../components/NudosComponents';
import { formatShortDate } from '../../../../utils/formatDatesAndHours';
import { formatRoundedUpNumber } from '../../../../utils/formatNumbers';
import { GoToDetailsArrow } from '../../../../assets/images/orders';

import { IStockOrderData, IStockOrderShipmentStatusName, SHIPMENT_STATUS_STOCK_ORDER } from '../../../../types/stockOrders';
import BordOneToneIcon from '../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import CommentsModalTrigger from './CommentsModalTrigger/CommentsModalTrigger';

import './StockOrderCard.scss';

interface IStockOrderCard {
  data: IStockOrderData;
  refreshData?: () => void;
  isConfirmedColumn?: boolean;
  shipmentStatusName: IStockOrderShipmentStatusName;
}

/**
 * Componente que representa una tarjeta de orden de stock
 * @param {IStockOrderCard} props - Props del componente
 * @param {IStockOrderData} props.data - Datos de la orden de stock
 * @param {Function} props.refreshData - Función para actualizar los datos
 */
const StockOrderCard = ({ data, isConfirmedColumn, shipmentStatusName }: IStockOrderCard) => {
  const {
    orderId,
    uuid,
    providerName,
    country,
    totalValue,
    currency,
    createdAt,
    canceledAt,
    nextDeliveryDate,
    deliveryDate,
    shipments,
    stockOrderComments
  } = data;

  const shipmentConfirmedEmpty = shipments?.confirmed === 0;
  const shipmentDeliveryProcessEmpty = shipments?.deliveryProcess === 0;
  const shipmentDelayedEmpty = shipments?.delayed === 0;
  const shipmentDeliveredEmpty = shipments?.delivered === 0;

  const formattedCreationDate = createdAt ? formatShortDate(createdAt)?.onlyDate : 'Sin fecha';
  const formattedDeliveryDate = deliveryDate ? formatShortDate(deliveryDate)?.textWithTimezone : 'Sin fecha';
  const formattedEstimatedDate = nextDeliveryDate
    ? formatShortDate(nextDeliveryDate)?.textWithTimezone
    : 'Sin fecha estimada';
  const formattedCanceledAt = canceledAt ? formatShortDate(canceledAt)?.onlyDate : 'Sin fecha';

  const getStatusStyles = () => {
    switch (shipmentStatusName) {
      case SHIPMENT_STATUS_STOCK_ORDER.CONFIRMED:
        return '';
      case SHIPMENT_STATUS_STOCK_ORDER.DELIVERY_PROCESS:
        return 'inProcess';
      case SHIPMENT_STATUS_STOCK_ORDER.DELAYED:
        return 'delayed';
      case SHIPMENT_STATUS_STOCK_ORDER.DELIVERED:
        return 'delivered';
      case SHIPMENT_STATUS_STOCK_ORDER.CANCELLED:
        return 'cancelled';
      default:
        return '';
    }
  };

  return (
    <div className={`stockOrderCard ${getStatusStyles()}`}>
      <section className="headerSection">
        <div className="providerNameAndFlag">
          <div className="flag" style={{ backgroundImage: `url(${country?.flag})` }} />
          <div className={`text-xs font-semibold ${getStatusStyles()}`}>{`Bodegas ${country?.name}`}</div>
        </div>
        <div className="orderNumberAndArrow">
          <Link className="replaceRedirectionLink" to={`/stock/details/${uuid}`} />
          <div className="orderNumber">Orden N°{orderId}</div>
          <GoToDetailsArrow className="goToDetailsArrow" />
        </div>
      </section>

      <section className="bodySection">
        <div className="leftData">
          {!canceledAt && !isConfirmedColumn && (
            <div>
              {deliveryDate ? (
                <div className="deliveryDate text-xs">Entregado: {formattedDeliveryDate}</div>
              ) : (
                <div className="estimatedDate text-xs">Estimada: {formattedEstimatedDate}</div>
              )}
            </div>
          )}

          {canceledAt && <div className="canceledAt text-xs">Fecha de cancelación: {formattedCanceledAt}</div>}

          <NudosHoverText
            customClassName={`providerName ${isConfirmedColumn ? 'mb-6' : 'mt-2'}`}
            text={providerName || 'Sin nombre del proveedor'}
            charactersLimit={35}
            onlyIfTruncated
          />

          <CommentsModalTrigger orderId={orderId} uuid={uuid} stockOrderComments={stockOrderComments} />
        </div>

        <div className="rightData">
          <div className="dates">
            <div className="creationDate">{formattedCreationDate}</div>
          </div>
          <div className="costData">
            <div className="totalCost">
              ${formatRoundedUpNumber(Number(totalValue), 2)} {currency}
            </div>
          </div>
          <div className="shipmentsCountContainer justify-end">
            {!shipmentConfirmedEmpty && (
              <div className="singleShipmentCount confirmed">
                <BordOneToneIcon variant="minusCircle" standardSize={8} />
                <span>{shipments?.confirmed}</span>
              </div>
            )}

            {!shipmentDeliveryProcessEmpty && (
              <div className="singleShipmentCount deliveryProcess">
                <BordOneToneIcon variant="clock" standardSize={8} />
                <span>{shipments?.deliveryProcess}</span>
              </div>
            )}
            {!shipmentDelayedEmpty && (
              <div className="singleShipmentCount delayed">
                <BordOneToneIcon variant="alertTriangleWarning" standardSize={8} />
                <span>{shipments?.delayed}</span>
              </div>
            )}
            {!shipmentDeliveredEmpty && (
              <div className="singleShipmentCount delivered">
                <BordOneToneIcon variant="checkCircle" standardSize={8} />
                <span>{shipments?.delivered}</span>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default StockOrderCard;
